// AGC: Backend Logs Admin Component
// AGC: Displays backend server logs for debugging and monitoring

import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertTriangle, Check, X } from 'lucide-react'
import { getBackendUrl } from '../../utils/backendUrl'

/**
 * AGC: BackendLogsAdmin - Admin panel for viewing backend logs
 */
export const BackendLogsAdmin: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null)

  /**
   * AGC: Fetch logs from backend API
   */
  const fetchLogs = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/logs`)

      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && Array.isArray(data.logs)) {
        setLogs(data.logs)
      } else {
        throw new Error('Invalid logs format')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to fetch logs: ${message}`)
      console.error('❌ AGC: Error fetching logs:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * AGC: Toggle auto-refresh
   */
  useEffect(() => {
    if (autoRefresh) {
      // AGC: Fetch immediately, then set interval
      fetchLogs()
      const interval = setInterval(() => {
        fetchLogs()
      }, 5000) // AGC: Refresh every 5 seconds
      setRefreshInterval(interval)
    } else {
      // AGC: Clear interval when auto-refresh is disabled
      if (refreshInterval) {
        clearInterval(refreshInterval)
        setRefreshInterval(null)
      }
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    }
  }, [autoRefresh]) // eslint-disable-line react-hooks/exhaustive-deps

  // AGC: Fetch logs on mount
  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  return (
    <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none">
      {/* AGC: Header */}
      <div className="p-6 border-b border-slate-200 dark:border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🖥️</span>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">Backend Logs</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                autoRefresh
                  ? 'bg-brand-primary dark:bg-brand-light text-white hover:bg-brand-dark dark:hover:opacity-90'
                  : 'bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-white/[0.15]'
              }`}
            >
              {autoRefresh ? '⏸️ Auto-refresh ON' : '▶️ Auto-refresh OFF'}
            </button>
            
            <button
              onClick={fetchLogs}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-info-700 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:opacity-90 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* AGC: Error message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* AGC: Logs display */}
      <div className="p-6">
        {isLoading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400 dark:text-slate-500" />
            <span className="ml-3 text-slate-500 dark:text-slate-400">Loading logs...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 dark:text-slate-400">
            <AlertTriangle className="w-12 h-12 mb-4" />
            <p className="text-lg font-medium">No logs available</p>
            <p className="text-sm mt-2">Backend server may not be running or logs endpoint is not available.</p>
          </div>
        ) : (
          <div className="font-mono bg-slate-50 dark:bg-admin-bg-dark text-slate-700 dark:text-slate-300 text-sm rounded-lg p-4 overflow-auto max-h-[600px]">
            {logs.map((log, index) => (
              <div key={index} className="mb-1">
                {log}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default BackendLogsAdmin



